/**
 * Synchronous script runner — the "Run" affordance on the script detail page.
 *
 * v1 shape:
 *   - Operator picks an Environment (from the script's owning application).
 *   - Operator picks an Access Method (from any org reachable through the org
 *     tree). Later this becomes a derived value from the placement's server
 *     once `servers.access_method_id` lands; today it's an explicit choice so
 *     the same surface works for one-off testing too.
 *   - If the script declares params (paramSchema has `properties`), each one
 *     gets an Input row. Schema is JSON-Schema-ish (`properties` + `required`).
 *   - Run button POSTs to /api/v1/scripts/:id/run. On success/failure we render
 *     stdout/stderr in a terminal-style panel, with the resolved command and
 *     exit code/duration in a small results header.
 *
 * Queued scripts: the panel disables itself with an explainer — queued mode
 * ships in its own round.
 *
 * No ChangePreview / typed-confirmation yet — the architecture calls for one
 * before destructive runs, but it has its own infra to build. v1 keeps the
 * Run button direct; the preview surface layers on top later without changing
 * this endpoint.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Play, AlertCircle, Check, X } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import {
  api,
  ApiError,
  type AllowedEnvironments,
  type Script,
  type ScriptRunInput,
  type ScriptRunResult,
} from '@/api/client.js';

/**
 * True if the env matches the script's allow-list. Mirrors the server check
 * exactly (slugs OR kinds; null ⇒ everything allowed). Keeping the rule in
 * one short helper makes "what does the UI think is runnable?" obvious.
 */
function envAllowed(env: { slug: string; kind: string }, allow: AllowedEnvironments | null): boolean {
  if (!allow) return true;
  const okBySlug = allow.slugs?.includes(env.slug) ?? false;
  const okByKind = allow.kinds?.includes(env.kind as NonNullable<AllowedEnvironments['kinds']>[number]) ?? false;
  return okBySlug || okByKind;
}

/**
 * Render the allow-list as the tooltip text shown next to a disabled env
 * option. Mirrors the server's 422 message so operators see the same
 * sentence whether they trip the SPA gate or the API one.
 */
function describeAllowList(allow: AllowedEnvironments): string {
  const parts: string[] = [];
  if (allow.slugs && allow.slugs.length > 0) parts.push(`slug ∈ {${allow.slugs.join(', ')}}`);
  if (allow.kinds && allow.kinds.length > 0) parts.push(`kind ∈ {${allow.kinds.join(', ')}}`);
  return parts.length > 0 ? `Allowed: ${parts.join(' OR ')}` : 'Script effectively disabled — empty allow-list';
}
import { cn } from '@/lib/utils.js';

interface Props {
  script: Script;
  /** Application id is needed to scope environments + access methods. */
  applicationId: string;
}

interface ParamDecl {
  name: string;
  required: boolean;
  description?: string;
}

/**
 * Best-effort parse of the script's paramSchema. Accepts the JSON-Schema
 * shape (`{ properties: { foo: {...} }, required: ['foo'] }`); anything else
 * collapses to "no declared params, accept whatever the operator types".
 */
function parseParamSchema(schema: unknown): ParamDecl[] {
  if (!schema || typeof schema !== 'object') return [];
  const s = schema as { properties?: Record<string, unknown>; required?: string[] };
  if (!s.properties) return [];
  const required = new Set(s.required ?? []);
  return Object.entries(s.properties).map(([name, def]) => {
    const decl: ParamDecl = { name, required: required.has(name) };
    if (def && typeof def === 'object' && 'description' in def) {
      const d = (def as { description?: unknown }).description;
      if (typeof d === 'string') decl.description = d;
    }
    return decl;
  });
}

export function ScriptRunPanel({ script, applicationId }: Props): JSX.Element {
  const envsQuery = useQuery({
    queryKey: ['environments', 'app', applicationId],
    queryFn: () => api.environments.forApp(applicationId),
    enabled: !!applicationId,
  });
  const envs = envsQuery.data ?? [];

  const accessMethodsQuery = useQuery({
    queryKey: ['accessMethods', 'all'],
    queryFn: () => api.accessMethods.listAll(),
  });
  const accessMethods = accessMethodsQuery.data ?? [];

  const paramDecls = useMemo(() => parseParamSchema(script.paramSchema), [script.paramSchema]);

  const [environmentId, setEnvironmentId] = useState('');
  const [accessMethodId, setAccessMethodId] = useState('');
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScriptRunResult | null>(null);

  // Auto-select the first ALLOWED env / access method when the lists arrive.
  // If the current selection isn't allowed by the script we re-pick — keeps the
  // "click Run" path from immediately failing when an operator opens the panel
  // on a restricted script for the first time.
  useEffect(() => {
    if (envs.length === 0) return;
    const currentOk =
      environmentId &&
      envs.some(
        (e: { id: string; slug: string; kind: string }) =>
          e.id === environmentId && envAllowed(e, script.allowedEnvironments),
      );
    if (!currentOk) {
      const firstAllowed = envs.find((e: { id: string; slug: string; kind: string }) =>
        envAllowed(e, script.allowedEnvironments),
      );
      // Fall back to the first env even if none allowed, so the dropdown has a
      // value — the Run button is gated separately on `selectedEnvIsAllowed`.
      setEnvironmentId((firstAllowed ?? envs[0]!).id);
    }
  }, [envs, environmentId, script.allowedEnvironments]);
  useEffect(() => {
    if (
      accessMethods.length > 0 &&
      !accessMethods.some((a: { id: string }) => a.id === accessMethodId)
    ) {
      setAccessMethodId(accessMethods[0]!.id);
    }
  }, [accessMethods, accessMethodId]);

  const queued = script.executionMode === 'queued';
  // Whether the currently-selected env passes the script's allow-list.
  // Re-derived on every render so changing the dropdown updates the button state.
  const selectedEnv = envs.find(
    (e: { id: string; slug: string; kind: string }) => e.id === environmentId,
  ) as { id: string; slug: string; kind: string } | undefined;
  const selectedEnvIsAllowed = selectedEnv
    ? envAllowed(selectedEnv, script.allowedEnvironments)
    : false;

  async function run() {
    if (!environmentId || !accessMethodId) {
      setError('Pick an environment and an access method.');
      return;
    }
    // Defensive — the Run button is disabled when the selected env isn't
    // allowed, but if someone bypasses that we refuse before hitting the API.
    if (!selectedEnvIsAllowed && script.allowedEnvironments) {
      setError(
        `This script is not allowed to run in the selected environment. ${describeAllowList(
          script.allowedEnvironments,
        )}.`,
      );
      return;
    }
    // Validate required params client-side too.
    const missing = paramDecls.filter((p) => p.required && !paramValues[p.name]).map((p) => p.name);
    if (missing.length > 0) {
      setError(`Missing required parameters: ${missing.join(', ')}`);
      return;
    }
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const params: Record<string, string> = {};
      for (const [k, v] of Object.entries(paramValues)) {
        if (v !== '') params[k] = v;
      }
      // exactOptionalPropertyTypes: only set `params` when we have some — omit otherwise.
      const input: ScriptRunInput = { environmentId, accessMethodId };
      if (Object.keys(params).length > 0) input.params = params;
      const out = await api.scripts.run(script.id, input);
      setResult(out);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not run script.');
    } finally {
      setRunning(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Play className="h-5 w-5" />
          Run
        </CardTitle>
        <CardDescription>
          Resolves the placement for the chosen environment, decrypts the access-method&apos;s
          leaf credential, substitutes <span className="font-mono">{'{{param}}'}</span> placeholders,
          and runs once. stdout, stderr, exit code, and duration come back below; an audit
          entry is written either way.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {queued ? (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
            This script is declared <span className="font-mono">queued</span>. Queued
            execution (BullMQ-backed) lands in a follow-up. Edit the script and switch to
            <span className="font-mono"> sync</span> mode for now.
          </p>
        ) : null}

        {script.allowedEnvironments && (
          <p
            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs"
            title={describeAllowList(script.allowedEnvironments)}
          >
            <span className="font-semibold">Env restriction:</span>{' '}
            {describeAllowList(script.allowedEnvironments)}. Other environments are disabled below.
          </p>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="env">Environment</Label>
            <select
              id="env"
              value={environmentId}
              onChange={(e) => setEnvironmentId(e.target.value)}
              disabled={queued || envs.length === 0}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {envs.length === 0 && <option value="">(no environments)</option>}
              {envs.map((env: { id: string; name: string; slug: string; kind: string }) => {
                const allowed = envAllowed(env, script.allowedEnvironments);
                return (
                  <option key={env.id} value={env.id} disabled={!allowed}>
                    {env.name} ({env.kind}){!allowed ? ' — not allowed by this script' : ''}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="am">Access method</Label>
            <select
              id="am"
              value={accessMethodId}
              onChange={(e) => setAccessMethodId(e.target.value)}
              disabled={queued || accessMethods.length === 0}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {accessMethods.length === 0 && <option value="">(no access methods)</option>}
              {accessMethods.map((am: { id: string; name: string; kind: string }) => (
                <option key={am.id} value={am.id}>
                  {am.name} ({am.kind})
                </option>
              ))}
            </select>
          </div>
        </div>

        {paramDecls.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Parameters
            </p>
            {paramDecls.map((p) => (
              <div key={p.name} className="space-y-1">
                <Label htmlFor={`p-${p.name}`} className="text-sm">
                  {p.name}
                  {p.required && <span className="ml-1 text-destructive">*</span>}
                </Label>
                <Input
                  id={`p-${p.name}`}
                  value={paramValues[p.name] ?? ''}
                  onChange={(e) =>
                    setParamValues((prev) => ({ ...prev, [p.name]: e.target.value }))
                  }
                  disabled={queued}
                  placeholder={p.description ?? ''}
                />
              </div>
            ))}
          </div>
        )}

        {error && (
          <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {error}
          </p>
        )}

        <div>
          <Button
            onClick={run}
            disabled={
              running || queued || !environmentId || !accessMethodId || !selectedEnvIsAllowed
            }
            title={
              !selectedEnvIsAllowed && script.allowedEnvironments
                ? describeAllowList(script.allowedEnvironments)
                : undefined
            }
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            {running ? 'Running…' : 'Run'}
          </Button>
        </div>

        {result && (
          <div className="space-y-2 pt-2">
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-medium',
                  result.success
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                    : 'border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400',
                )}
              >
                {result.success ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                exit {result.exitCode ?? 'n/a'}
              </span>
              <span className="text-muted-foreground">{result.durationMs} ms</span>
              <span className="font-mono text-[11px] text-muted-foreground">
                {result.chainSummary}
              </span>
              {result.errorCode && (
                <span className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-mono text-[11px] text-amber-600 dark:text-amber-400">
                  {result.errorCode}
                </span>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Resolved command
              </p>
              <pre className="overflow-x-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-[11px]">
                {result.resolvedCommand}
              </pre>
            </div>
            {result.stdout && (
              <div className="space-y-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  stdout
                </p>
                <pre className="max-h-[40vh] overflow-auto rounded-md border border-border bg-zinc-950 p-3 font-mono text-[11px] text-zinc-100">
                  {result.stdout}
                </pre>
              </div>
            )}
            {result.stderr && (
              <div className="space-y-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  stderr
                </p>
                <pre className="max-h-[40vh] overflow-auto rounded-md border border-border bg-zinc-950 p-3 font-mono text-[11px] text-rose-300">
                  {result.stderr}
                </pre>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
